import "./css/LandingPage.css";
import { useEffect, useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { hasActiveSession, setSessionData } from "../utils/session";


async function readError(response) {
    const text = await response.text();
    if (!text) return null;
    try {
        return JSON.parse(text);
    } catch {
        return { message: text };
    }
}

export default function LandingPage() {
    const navigate = useNavigate();
    const [searchParams] = useSearchParams();
    const [username, setUsername] = useState("");
    const [password, setPassword] = useState("");
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState({ text: "", html: "" });

    useEffect(() => {
        if (hasActiveSession()) {
            navigate("/dashboard", { replace: true });
        }
    }, [navigate]);

    // Pick up any SSO error forwarded via ?samlError=...
    useEffect(() => {
        const samlError = searchParams.get("samlError");
        if (samlError) {
            setError({ text: decodeURIComponent(samlError), html: "" });
        }
    }, [searchParams]);

    const handleLogin = async () => {
        setLoading(true);

        try {
            const response = await fetch(`${process.env.REACT_APP_API_URL}/authenticate/login`, {
                method: "POST",
                headers: {
                    "Content-Type": "application/json",
                    Accept: "application/json",
                },
                body: JSON.stringify({ username, password }),
            });

            if (response.ok) {
                const data = await response.json();
                let ibmToken = "";

                const rawProfilerUrl =
                    process.env.REACT_APP_PROFILER_URL ||
                    "https://3di-profiler.net/authServlet?username=[username]&password=[password]&redirect=[redirectid]";

                const profilerUrl = rawProfilerUrl
                    .replace("[username]", encodeURIComponent(username))
                    .replace("[password]", encodeURIComponent(password));

                try {
                    ibmToken = data.ibmToken;
                } catch (ibmError) {
                    console.warn("Unable to prefetch IBM token during login.", ibmError);
                }

                setSessionData({
                    token: data.token,
                    ibmToken,
                    user: data.user,
                    userName: username,
                    userId: data.userId,
                    profilerUrl,
                    ibmRestricted: data.ibmRestricted,
                    xtvmAllowed: data.xtvmAllowed,
                    altDataAllowed: data.altDataAllowed,
                    ibmAllowed: data.ibmAllowed,
                });
                navigate("/dashboard", { replace: true });
                return;
            }

            const err = await readError(response);
            const html = err?.messageHtml || "";
            const text = err?.message || `Login failed (${response.status})`;
            setError({text, html});
        } catch (e) {
            console.error(e);
            setError({text: "An error occurred. Please try again.", html: ""})
        } finally {
            setLoading(false);
        }
    };

    const goToSSO = async () => {
        const email = window.prompt("Enter your work email address:");
        if (!email || !email.includes("@")) return;

        try {
            const res = await fetch(
                `${process.env.REACT_APP_API_URL}/saml/initiate?email=${encodeURIComponent(email)}`
            );
            const data = await res.json();

            if (!res.ok) {
                setError({ text: data.error || "SSO not configured for this domain.", html: "" });
                return;
            }

            window.location.href = `${process.env.REACT_APP_API_URL}/saml/login`;
        } catch (e) {
            setError({ text: "SSO lookup failed. Please try again.", html: "" });
        }
    };

    const showError = Boolean(error.text || error.html);

    return (
        <div className="landing-container">
            <img src={process.env.PUBLIC_URL + "/logo.png"} alt="" />

            <div className="title-with-version">
                <h1 className="title">Welcome to Profiler</h1>
                <span className="version">v8</span>
            </div>

            <div className="login-form">
                {showError ? (
                    <div className="login-error">
                        {error.html ? (
                            <span dangerouslySetInnerHTML={{ __html: error.html }} />
                        ) : (
                            error.text
                        )}
                    </div>
                ) : null}

                <input
                    type="text"
                    placeholder="Username"
                    value={username}
                    onChange={(e) => setUsername(e.target.value)}
                    className="input-field"
                />
                <input
                    type="password"
                    placeholder="Password"
                    value={password}
                    onChange={(e) => setPassword(e.target.value)}
                    className="input-field"
                    onKeyDown={(e) => e.key === "Enter" && handleLogin()}
                />

                {showError ? (
                    <div className="login-error">
                        {error.html ? (
                            <span dangerouslySetInnerHTML={{ __html: error.html }} />
                        ) : (
                            error.text
                        )}
                    </div>
                ) : null}

                <button className="login-btn" onClick={handleLogin} disabled={loading}>
                    {loading ? "Logging in..." : "Login"}
                </button>
                {/* <button className="sso-btn" onClick={goToSSO} disabled={loading}>
                    {loading ? "Diverting..." : "SSO Login"}
                </button> 
                */}
            </div>
        </div>
    );
}
